/**
 * ShareMyWeekCard — picks the highest-impact metric in the window and turns it
 * into a one-click tweet. The intent URL pre-fills X/Twitter's compose box with
 * the user's metric + a link back to NEXUS. Also offers clipboard copy.
 *
 * Rendered only when totals are non-trivial (reactions ≥ 10 OR revenue ≥ $1).
 */
import { useState } from "react";
import { Share2, Copy, Check, Twitter } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function pickHeadline(totals, days) {
  const reactions = Number(totals?.reactions || 0);
  const revenue = Number(totals?.revenue_net || 0);
  const posts = Number(totals?.posts || 0);
  const window = days === 7 ? "this week" : `last ${days} days`;
  if (revenue >= 100) {
    return `Just earned $${revenue.toFixed(0)} on NEXUS ${window} 💸 — building my digital empire one post at a time.`;
  }
  if (revenue >= 1) {
    return `My creator economy is alive: $${revenue.toFixed(2)} earned on NEXUS ${window} 🚀`;
  }
  if (reactions >= 100) {
    return `My NEXUS posts pulled ${reactions} reactions ${window} 🔥 — the algorithm is paying attention.`;
  }
  if (reactions >= 10) {
    return `${reactions} reactions on NEXUS ${window} across ${posts} posts. Compounding. ⚡`;
  }
  return `Shipping on NEXUS this week — building in public, one post at a time.`;
}

export default function ShareMyWeekCard({ totals, days }) {
  const [copied, setCopied] = useState(false);
  const reactions = Number(totals?.reactions || 0);
  const revenue = Number(totals?.revenue_net || 0);
  if (reactions < 10 && revenue < 1) return null;

  const headline = pickHeadline(totals, days);
  const shareUrl = BACKEND_URL || "https://nexus.ai";
  const text = `${headline}\n\n→ ${shareUrl}`;
  const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  return (
    <div
      data-testid="share-my-week-card"
      className="rounded-2xl p-5 border border-[#1DA1F2]/20"
      style={{
        background: "linear-gradient(135deg, rgba(29,161,242,0.06) 0%, rgba(221,42,123,0.06) 60%, rgba(245,158,11,0.06) 100%)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(29,161,242,0.12)" }}
        >
          <Share2 size={18} className="text-[#1DA1F2]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#1DA1F2] font-bold">
            Tweet your week
          </div>
          <div className="font-display font-bold text-base mt-1 text-[#0F172A]">
            Your wins, pre-written.
          </div>
          <div
            data-testid="share-my-week-preview"
            className="mt-2 text-sm text-[#0F172A] bg-white/60 backdrop-blur rounded-xl p-3 leading-relaxed whitespace-pre-wrap border border-white/40"
          >
            {headline}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              data-testid="share-my-week-tweet"
              href={tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white"
              style={{ background: "#1DA1F2" }}
            >
              <Twitter size={13} /> Post on X
            </a>
            <button
              data-testid="share-my-week-copy"
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border border-slate-300 text-[#0F172A] bg-white hover:bg-slate-50"
            >
              {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy text"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
